// @flow
import get from 'lodash/get';
import set from 'lodash/set';
import { createSelector } from 'reselect';

import {
  selectCartUUID,
  selectDiscounts,
  addCartDiscount,
  addCartProduct,
  type ProductData,
} from '@gandi/cart';

import {
  selectIsLoaded as selectIsLoadedCoupons,
  selectOne as selectOneCoupon,
  fetchAll as fetchAllCoupons,
  type Coupon,
} from '@gandi/redux-resource-coupon';
import { instances } from '@gandi/redux-resource-simplehosting';
import { selectCurrentId } from '@gandi/redux-sharing';
import type { FetchResponse } from '@gandi/resource/redux';

import type { TranslatorProps } from '@gandi/translate';

import { createLogger } from 'gandi.redux-logger';

import type { Dispatch, FullState } from '../../types';

// ------------------------------------------------------------------- types

type GetState = () => FullState;
type FetchResponseError = FetchResponse<
  mixed,
  {
    errors: Array<{ description: string, location: string, name: string, ... }>,
    ...
  },
>;

/**
 * extract error messages from REST API response.
 */
const parseCartErrors = (
  response: FetchResponseError,
): {
  _error: mixed,
  ...
} => {
  let errors = {};
  if (response && response.data && response.data.errors) {
    // map errors to the form expected format
    errors = response.data.errors.reduce((acc, error) => {
      set(acc, error.name, error.description);
      if (error.location !== 'body' && !('_error' in acc)) {
        // $FlowIgnore[prop-missing]
        acc._error = error.description;
      }
      return acc;
    }, {});
    // $FlowFixMe[prop-missing]
  } else if (response && response.data && response.data.message) {
    errors = {
      _error: response.data.message,
    };
  }

  // $FlowIgnore[prop-missing]
  return errors;
};

export class DryRunError extends Error {
  errors: { [key: string]: string, ... };
  response: FetchResponseError;

  constructor(message: any, response: FetchResponseError) {
    super(message);
    this.response = response;
    // $FlowFixMe[incompatible-type]
    this.errors = parseCartErrors(response);
  }
}

export const loggerNamespace = 'SimpleHosting/Renew/module';
const logger = createLogger(loggerNamespace);

// ---------------------------------------------------------- Coupon PAAS50*

// When a coupon PAAS50* is available, and the renewal is about a trial
// instance, we should add the coupon to cart.

const selectIsCouponInCart: (state: FullState, props: string) => boolean =
  // $FlowFixMe[incompatible-call]
  createSelector(
    [
      (state: FullState) => selectDiscounts(state),
      (_: any, props: string) => props,
    ],
    (discounts: Array<{ code: string, ... }>, code: string) =>
      discounts.filter(d => d.code.indexOf(code) === 0).length > 0,
  );

const selectAvailableCoupon: (state: FullState, props: string) => ?Coupon = (
  state: FullState,
  props: string = 'PAAS50*',
): ?Coupon =>
  // $FlowFixMe[prop-missing]
  selectOneCoupon(state, {
    consumed: 0,
    code: props,
  });

export const loadCouponsIfRequired =
  (): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch: Dispatch, getState: GetState) => {
    if (!selectCurrentId(getState())) return Promise.resolve();
    // $FlowFixMe[prop-missing]
    if (selectIsLoadedCoupons(getState())) return Promise.resolve();

    const sharingId = selectCurrentId(getState());
    return dispatch(fetchAllCoupons({ sharingId }));
  };

const applyCouponIfAvailable =
  (): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch: Dispatch, getState: GetState) => {
    // User needs to be authenticated
    if (!selectCurrentId(getState())) return Promise.resolve();

    const coupon = selectAvailableCoupon(getState(), 'PAAS50*');
    if (!coupon) return Promise.resolve();
    if (selectIsCouponInCart(getState(), coupon.code)) return Promise.resolve();

    return dispatch(addCartDiscount(coupon.code));
  };

// ------------------------------------------------------------------- Instances

export const loadInstance =
  (
    instanceId: string,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const isFetching = instances.selectIsFetchingOne(state, instanceId);
    const instanceExists =
      instances.selectIsLoadedOne(state, instanceId) && !isFetching;

    if (instanceExists) {
      return Promise.resolve();
    }

    // instance is not here yet, but should be fetching from a previous action.
    // The only possibility to pass previous condition without instance not
    // being available is when `isFetching` is true before dispatching current
    // action. If this happens, then we have to wait for the instance to be
    // available before retriggering the action.
    if (isFetching) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(dispatch(loadInstance(instanceId)));
        }, 300);
      });
    }

    return dispatch(instances.fetchOne(instanceId)).catch(err => {
      const statusCode = get(err, 'response.status');
      let message;

      switch (statusCode) {
        case 404:
          message = `Attempted to renew a non-existent instance (${instanceId}).`;
          break;
        case 403:
          message = `Attempted to renew a instance without necessary permissions (${instanceId}).`;
          break;
        default:
          message = `Unexpected error while fetching instance data (${instanceId}).`;
          break;
      }

      dispatch(logger.error({ res: err.response, err }, message));

      throw err;
    });
  };

// --------------------------------------------------------------------- Renewal

const doInstanceRenew =
  (
    translator: TranslatorProps,
    identifier: string,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<mixed>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const instance = instances.selectInstance(state, identifier);

    if (!instance) {
      return Promise.reject(new Error('Instance was not found.'));
    }

    const duration = 12;
    const duration_unit = 'm';

    const label = instance.name;
    const isTrial = instances.selectIsTrial(state, identifier);

    const productData: ProductData = {
      product_type: 'paas',
      process: 'renew',
      identifier,
      payload: {
        duration,
        duration_unit,

        // Not used by any API, only stored in the product payload for convenience.
        // Note that this will not be used in the cart widget.
        label,
        isTrial,
      },
    };

    return dispatch(addCartProduct({ productData, extra: { label } })) // This label is for cart widget
      .then(response => {
        const data = response.data;
        const status = data.status;
        const productId = data.id;
        const errors = data.errors;
        const cartId: string = selectCartUUID(getState()) || '';

        let error;
        if (status === 'error' || (Array.isArray(errors) && errors.length)) {
          error = new DryRunError(
            'Simple Hosting Instance Renew Dry-Run Errors',
            response,
          );
        }

        return Promise.resolve({ productId, cartId, error });
      });
  };

export const instanceRenew =
  (
    translator: TranslatorProps,
    instanceId: string,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<mixed>) =>
  (dispatch: Dispatch, getState: GetState) =>
    Promise.resolve()
      .then(() => dispatch(loadInstance(instanceId)))
      .then(() => {
        // if the instance is a trial, we need to make sure discounts are
        // available.
        const isTrial = instances.selectIsTrial(getState(), instanceId);
        if (!isTrial) return undefined;

        return dispatch(loadCouponsIfRequired()).catch(err => {
          dispatch(
            logger.error(
              { err, body: { instanceId } },
              'Unable to load coupons for user.',
            ),
          );
          // error can be ignored, we just log.
        });
      })
      .then(() => {
        // coupons should be in cart BEFORE product.
        // so if coupon is available, and instance is a trial,
        // we should add the discount.
        const isTrial = instances.selectIsTrial(getState(), instanceId);
        // attempt to apply a coupon if:
        //   - the instance is a trial
        //   - coupons are available
        //   - renewal is in cart.
        if (!isTrial) return Promise.resolve();

        return dispatch(applyCouponIfAvailable()).catch(err => {
          dispatch(
            logger.error(
              { err, body: { instanceId } },
              'Unable to apply coupons for user.',
            ),
          );
        });
      })
      .then(() => dispatch(doInstanceRenew(translator, instanceId)))
      .catch(err => {
        // any error with no 'response' field is not a resource error
        // and therefore does not come from the API.
        // in such a case, we need to log the error.
        if (!('response' in err)) {
          dispatch(
            logger.error(
              { err, body: { instanceId } },
              'Unexpected error while renewing instance.',
            ),
          );
        }

        throw err;
      });
